/**
 * WebRTC transport. A star around the host: every guest holds one data channel
 * to the host, and guests never talk to each other.
 *
 * The channel is unordered with no retransmits, because a late input or
 * snapshot is worthless -- the next one is already on its way.
 */
import {
  type SignalMessage,
  type Signaling,
  addressedTo,
} from './signaling.js';

/**
 * A public STUN server for peers on different networks. Two machines on the
 * office LAN pair up on host candidates alone and never wait for it.
 */
export const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
];

const CHANNEL_LABEL = 'game';
const CHANNEL_INIT: RTCDataChannelInit = { ordered: false, maxRetransmits: 0 };

export type LinkState = 'connecting' | 'open' | 'closed' | 'failed';

export interface PeerLinkEvents {
  onOpen?: (link: PeerLink) => void;
  onClose?: (link: PeerLink) => void;
  onMessage?: (data: string, link: PeerLink) => void;
  onStateChange?: (state: LinkState, link: PeerLink) => void;
}

/** One peer-to-peer connection. */
export class PeerLink {
  readonly remoteId: string;
  state: LinkState = 'connecting';

  private readonly pc: RTCPeerConnection;
  private channel: RTCDataChannel | null = null;
  private readonly pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteDescriptionSet = false;

  constructor(
    remoteId: string,
    private readonly signaling: Signaling,
    private readonly events: PeerLinkEvents,
    iceServers: RTCIceServer[] = DEFAULT_ICE_SERVERS,
  ) {
    this.remoteId = remoteId;
    this.pc = new RTCPeerConnection({ iceServers });

    this.pc.onicecandidate = (event): void => {
      if (!event.candidate) return;
      void this.signaling.send({
        to: this.remoteId,
        kind: 'candidate',
        payload: event.candidate.toJSON(),
      });
    };

    this.pc.onconnectionstatechange = (): void => {
      const s = this.pc.connectionState;
      if (s === 'failed') this.setState('failed');
      else if (s === 'closed' || s === 'disconnected') this.setState('closed');
    };

    this.pc.ondatachannel = (event): void => {
      this.attachChannel(event.channel);
    };
  }

  private setState(state: LinkState): void {
    if (this.state === state) return;
    this.state = state;
    this.events.onStateChange?.(state, this);
    if (state === 'open') this.events.onOpen?.(this);
    if (state === 'closed' || state === 'failed') this.events.onClose?.(this);
  }

  private attachChannel(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.onopen = (): void => this.setState('open');
    channel.onclose = (): void => this.setState('closed');
    channel.onmessage = (event: MessageEvent): void => {
      if (typeof event.data === 'string') this.events.onMessage?.(event.data, this);
    };
  }

  /** Host side: create the channel and the offer. */
  async offer(): Promise<void> {
    this.attachChannel(this.pc.createDataChannel(CHANNEL_LABEL, CHANNEL_INIT));
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    await this.signaling.send({
      to: this.remoteId,
      kind: 'offer',
      payload: this.pc.localDescription?.toJSON() ?? offer,
    });
  }

  /** Guest side: answer an offer. */
  async answer(offer: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(offer);
    this.remoteDescriptionSet = true;
    await this.drainCandidates();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    await this.signaling.send({
      to: this.remoteId,
      kind: 'answer',
      payload: this.pc.localDescription?.toJSON() ?? answer,
    });
  }

  async acceptAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(answer);
    this.remoteDescriptionSet = true;
    await this.drainCandidates();
  }

  /**
   * Candidates can arrive before the description they belong to, so they are
   * held until there is somewhere to put them.
   */
  async addCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.remoteDescriptionSet) {
      this.pendingCandidates.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(candidate);
    } catch (err) {
      console.warn('[net] rejected ICE candidate', err);
    }
  }

  private async drainCandidates(): Promise<void> {
    const queued = this.pendingCandidates.splice(0);
    for (const candidate of queued) {
      try {
        await this.pc.addIceCandidate(candidate);
      } catch (err) {
        console.warn('[net] rejected buffered ICE candidate', err);
      }
    }
  }

  get isOpen(): boolean {
    return this.channel?.readyState === 'open';
  }

  send(data: string): boolean {
    if (!this.isOpen) return false;
    try {
      this.channel?.send(data);
      return true;
    } catch {
      // A full or closing channel is not worth throwing over.
      return false;
    }
  }

  close(): void {
    this.channel?.close();
    this.pc.close();
    this.setState('closed');
  }
}

export interface HostEvents {
  onPeerOpen?: (peerId: string) => void;
  onPeerClose?: (peerId: string) => void;
  onMessage?: (peerId: string, data: string) => void;
}

/** Accepts joins and keeps one link per guest. */
export class NetHost {
  private readonly links = new Map<string, PeerLink>();
  private unsubscribe: (() => void) | null = null;

  constructor(
    readonly signaling: Signaling,
    private readonly events: HostEvents = {},
    private readonly iceServers: RTCIceServer[] = DEFAULT_ICE_SERVERS,
  ) {}

  get peerIds(): string[] {
    return [...this.links.keys()];
  }

  get openCount(): number {
    return [...this.links.values()].filter((l) => l.isOpen).length;
  }

  async start(): Promise<void> {
    await this.signaling.start();
    this.unsubscribe = this.signaling.onMessage((m) => void this.handle(m));
    // Announce, so a guest that arrived first stops waiting and re-joins.
    await this.signaling.send({ to: null, kind: 'join' });
  }

  private async handle(message: SignalMessage): Promise<void> {
    if (!addressedTo(message, this.signaling.peerId)) return;
    switch (message.kind) {
      case 'join': {
        if (this.links.has(message.from)) return;
        const link = this.createLink(message.from);
        await link.offer();
        return;
      }
      case 'answer': {
        await this.links
          .get(message.from)
          ?.acceptAnswer(message.payload as RTCSessionDescriptionInit);
        return;
      }
      case 'candidate': {
        await this.links.get(message.from)?.addCandidate(message.payload as RTCIceCandidateInit);
        return;
      }
      case 'leave': {
        this.drop(message.from);
        return;
      }
      default:
        return;
    }
  }

  private createLink(peerId: string): PeerLink {
    const link = new PeerLink(
      peerId,
      this.signaling,
      {
        onOpen: () => this.events.onPeerOpen?.(peerId),
        onClose: () => this.drop(peerId),
        onMessage: (data) => this.events.onMessage?.(peerId, data),
      },
      this.iceServers,
    );
    this.links.set(peerId, link);
    return link;
  }

  private drop(peerId: string): void {
    const link = this.links.get(peerId);
    if (!link) return;
    this.links.delete(peerId);
    link.close();
    this.events.onPeerClose?.(peerId);
  }

  send(peerId: string, data: string): boolean {
    return this.links.get(peerId)?.send(data) ?? false;
  }

  broadcast(data: string): void {
    for (const link of this.links.values()) link.send(data);
  }

  close(): void {
    this.unsubscribe?.();
    for (const link of this.links.values()) link.close();
    this.links.clear();
    this.signaling.close();
  }
}

export interface GuestEvents {
  onOpen?: () => void;
  onClose?: () => void;
  onMessage?: (data: string) => void;
}

/** Joins a room and holds a single link to the host. */
export class NetGuest {
  private link: PeerLink | null = null;
  private unsubscribe: (() => void) | null = null;

  constructor(
    readonly signaling: Signaling,
    private readonly events: GuestEvents = {},
    private readonly iceServers: RTCIceServer[] = DEFAULT_ICE_SERVERS,
  ) {}

  get isOpen(): boolean {
    return this.link?.isOpen ?? false;
  }

  async start(): Promise<void> {
    await this.signaling.start();
    this.unsubscribe = this.signaling.onMessage((m) => void this.handle(m));
    await this.signaling.send({ to: null, kind: 'join' });
  }

  private async handle(message: SignalMessage): Promise<void> {
    if (!addressedTo(message, this.signaling.peerId)) return;
    switch (message.kind) {
      case 'offer': {
        // A second offer means the host restarted: drop the stale link.
        if (this.link && this.link.remoteId !== message.from) this.link.close();
        const link = new PeerLink(
          message.from,
          this.signaling,
          {
            onOpen: () => this.events.onOpen?.(),
            onClose: () => this.events.onClose?.(),
            onMessage: (data) => this.events.onMessage?.(data),
          },
          this.iceServers,
        );
        this.link = link;
        await link.answer(message.payload as RTCSessionDescriptionInit);
        return;
      }
      case 'candidate': {
        await this.link?.addCandidate(message.payload as RTCIceCandidateInit);
        return;
      }
      case 'leave': {
        if (this.link?.remoteId === message.from) {
          this.link.close();
          this.link = null;
        }
        return;
      }
      default:
        return;
    }
  }

  send(data: string): boolean {
    return this.link?.send(data) ?? false;
  }

  close(): void {
    this.unsubscribe?.();
    this.link?.close();
    this.link = null;
    this.signaling.close();
  }
}
